/**
 * useDeepLink — Bridges native deep-link events into React Router navigation.
 *
 * Supports both Tauri (desktop) and Capacitor (Android/iOS):
 *   - Tauri:     uses @tauri-apps/plugin-deep-link
 *   - Capacitor: uses @capacitor/app's appUrlOpen event
 *
 * When the system browser completes an OAuth flow, the OS invokes the
 * registered `flare://` URL scheme.  This hook listens for those events
 * and navigates the webview to the corresponding React route.
 *
 * On web builds this hook is a no-op.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IS_TAURI, IS_CAPACITOR } from '../config';

export function useDeepLink() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!IS_TAURI && !IS_CAPACITOR) return;

    let cleanup;

    (async () => {
      try {
        if (IS_TAURI) {
          // Tauri deep-link plugin
          const { onOpenUrl } = await import('@tauri-apps/plugin-deep-link');
          cleanup = await onOpenUrl((urls) => {
            for (const url of urls) {
              navigateFromUrl(url);
            }
          });
        } else if (IS_CAPACITOR) {
          // Capacitor App plugin — appUrlOpen fires on deep links
          const { App } = await import('@capacitor/app');
          const listener = await App.addListener('appUrlOpen', (event) => {
            navigateFromUrl(event.url);
          });
          cleanup = () => listener.remove();
        }
      } catch (err) {
        console.warn('[DeepLink] Plugin not available:', err);
      }
    })();

    function navigateFromUrl(url) {
      try {
        // url = "flare://auth/callback#token=xxx&email=..."
        const parsed = new URL(url);
        const path = parsed.pathname;  // "/auth/callback"
        const hash = parsed.hash;      // "#token=xxx&..."
        const search = parsed.search;  // "?error=..." (for error redirects)
        navigate(`${path}${search}${hash}`, { replace: true });
      } catch {
        console.warn('[DeepLink] Could not parse URL:', url);
      }
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, [navigate]);
}
